import { useRef, useCallback, useEffect } from 'react';
import type { DemoEvent } from '@/data/acme-demo';

interface UseDemoTimelineOptions {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  events: DemoEvent[];
  onEvent: (event: DemoEvent) => void;
  enabled: boolean;
}

/**
 * Fires DemoEvents based on elapsed time.
 * Uses video.currentTime when the video is loaded and playing.
 * Falls back to a wall-clock timer when no video is available.
 */
export function useDemoTimeline({
  videoRef,
  events,
  onEvent,
  enabled,
}: UseDemoTimelineOptions) {
  const firedRef = useRef<Set<number>>(new Set());
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef(0);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (enabled) {
      startTimeRef.current = performance.now();
    }
  }, [enabled]);

  const tick = useCallback(() => {
    const video = videoRef.current;
    const videoReady = video && video.readyState >= 2;

    let currentTime: number;

    if (videoReady && !video.paused && !video.ended) {
      currentTime = video.currentTime;
    } else if (videoReady && video.paused) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    } else {
      currentTime = (performance.now() - startTimeRef.current) / 1000;
    }

    for (let i = 0; i < events.length; i++) {
      if (firedRef.current.has(i)) continue;
      if (currentTime >= events[i].timestamp) {
        firedRef.current.add(i);
        onEventRef.current(events[i]);
      }
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [videoRef, events]);

  useEffect(() => {
    if (!enabled) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [enabled, tick]);

  const reset = useCallback(() => {
    firedRef.current = new Set();
    startTimeRef.current = performance.now();
  }, []);

  return { reset };
}
